/**
 * Decision Pack Generator
 * 
 * Comprehensive weekly pack with citations to diffs, impacts, and evidence.
 * Auto-loads data and presents it in a PMO/leadership-ready format.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Download, AlertTriangle, TrendingUp, TrendingDown, CheckCircle, XCircle } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API_BASE_URL } from '@/config';
import { getAuthHeaders } from '@/api/workosAuth';
import MainLayout from '@/components/layout/MainLayout';
import { EmptySnapshotState } from '@/components/project-state/EmptySnapshotState';

export const DecisionPack: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [loading, setLoading] = useState(true);
  const [packData, setPackData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      loadPackData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadPackData recreated each render; projectId is key
  }, [projectId]);

  const loadPackData = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      const [diffRes, impactsRes, violationsRes, snapshotsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/v1/diffs/${projectId}/weekly`, { credentials: 'include', headers }).catch(() => ({ ok: false })),
        fetch(`${API_BASE_URL}/v1/impacts/${projectId}`, { credentials: 'include', headers }).catch(() => ({ ok: false })),
        fetch(`${API_BASE_URL}/v1/policies/violations/${projectId}`, { credentials: 'include', headers }).catch(() => ({ ok: false })),
        fetch(`${API_BASE_URL}/v1/snapshots/${projectId}`, { credentials: 'include', headers }).catch(() => ({ ok: false }))
      ]);
      
      const diff = diffRes.ok && 'json' in diffRes ? await (diffRes as Response).json() : null;
      const impacts = impactsRes.ok && 'json' in impactsRes ? await (impactsRes as Response).json() : null;
      const violations = violationsRes.ok && 'json' in violationsRes ? await (violationsRes as Response).json() : null;
      const snapshots = snapshotsRes.ok && 'json' in snapshotsRes ? await (snapshotsRes as Response).json() : null;
      
      // Build comprehensive pack
      const pack = {
        generated_at: new Date().toISOString(),
        project_id: projectId,
        snapshot_count: snapshots?.snapshots?.length || 0,
        latest_snapshot: snapshots?.snapshots?.[0] || null,
        
        // Diff data
        diff_summary: diff?.summary || {},
        diff_message: diff?.message || null,
        requires_baseline: diff?.requires_baseline || false,
        activities_added: diff?.activities_added || [],
        activities_modified: diff?.activities_modified || [],
        milestone_changes: diff?.milestone_changes || [],
        float_changes: diff?.float_changes || [],
        
        // Impact data
        impacts_summary: impacts?.summary || {},
        milestone_threats: impacts?.milestone_threats || [],
        critical_path_impacts: impacts?.critical_path_impacts || [],
        constraint_bindings: impacts?.constraint_bindings || [],
        float_risks: impacts?.float_risks || [],
        
        // Compile status
        compile_status: violations?.overall_status || 'pending',
        violations: violations?.violations || [],
        violations_summary: violations?.summary || {},
        
        // Flags
        has_data: !!(diff || impacts || violations),
        has_changes: !!(diff && !diff.message && diff.summary?.total_changes > 0)
      };
      
      setPackData(pack);
    } catch (err) {
      console.error('Failed to load pack data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load decision pack data');
    } finally {
      setLoading(false);
    }
  };

  const exportPack = () => {
    if (!packData) return;
    
    const formatDate = (dateStr: string) => {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
    
    const content = `# Weekly Decision Pack
Generated: ${formatDate(packData.generated_at)}
Project: ${packData.latest_snapshot?.project_name || projectId}

## Executive Summary
${packData.has_changes ? `This week's schedule shows ${packData.diff_summary?.total_changes || 0} changes.` : 'No changes detected this week.'}
${packData.compile_status === 'pass' ? '✅ Schedule passes all quality checks.' : packData.compile_status === 'fail' ? '❌ Schedule fails quality checks - action required.' : '⚠️ Quality checks pending.'}

## Changes This Week
${packData.diff_summary?.total_changes || 0} total changes detected
- Activities Added: ${packData.activities_added?.length || 0}
- Activities Modified: ${packData.activities_modified?.length || 0}
- Milestone Changes: ${packData.milestone_changes?.length || 0}
- Float Changes: ${packData.float_changes?.filter((f: any) => f.crossed_threshold).length || 0}

## Milestone Threats
${packData.milestone_threats.length > 0 
  ? packData.milestone_threats.map((mt: any) => `- ${mt.milestone_name}: ${mt.threat_level} risk (${mt.days_at_risk || 0} days)`).join('\n')
  : 'No milestone threats identified.'}

## Critical Path Impacts
${packData.critical_path_impacts.length > 0
  ? packData.critical_path_impacts.map((cpi: any) => `- ${cpi.activity_name}: ${cpi.impact_type}`).join('\n')
  : 'No critical path impacts identified.'}

## Compile Status
Status: ${packData.compile_status.toUpperCase()}
Violations: ${packData.violations_summary?.total_violations || 0}
${packData.violations.length > 0 
  ? `\nTop Violations:\n${packData.violations.slice(0, 5).map((v: any) => `- ${v.rule_name}: ${v.severity}`).join('\n')}`
  : ''}

## Actions Required
${packData.violations.filter((v: any) => v.severity === 'critical').length > 0
  ? `⚠️ ${packData.violations.filter((v: any) => v.severity === 'critical').length} critical violations require immediate attention.`
  : 'No critical actions required.'}
`.trim();
    
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decision-pack-${projectId}-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <MainLayout title="Decision Pack">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nb-accent mx-auto"></div>
          <p className="mt-4 text-nb-text-muted">Loading decision pack...</p>
        </div>
      </MainLayout>
    );
  }

  if (error || !packData) {
    return (
      <MainLayout title="Decision Pack">
        <EmptySnapshotState
          title="Unable to Load Decision Pack"
          message={error || "Failed to load decision pack data. Please try again."}
        />
      </MainLayout>
    );
  }

  if (!packData.has_data || packData.diff_message) {
    return (
      <MainLayout title="Decision Pack">
        <EmptySnapshotState
          title="No Decision Pack Data Available"
          message={packData.diff_message || "Upload snapshots and set a baseline to generate a decision pack."}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Decision Pack">
      <div className="p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header with Export */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-1 text-nb-text-primary">Weekly Decision Pack</h1>
              <p className="text-sm text-nb-text-muted">
                Generated {new Date(packData.generated_at).toLocaleString()} • {packData.snapshot_count} snapshot{packData.snapshot_count !== 1 ? 's' : ''}
              </p>
            </div>
            <Button onClick={exportPack} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Pack
            </Button>
          </div>

          {/* Executive Summary */}
          <DarkPanel className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nb-text-primary">
              <FileText className="h-5 w-5" />
              Executive Summary
            </h2>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-nb-accent/10 rounded-lg border border-nb-accent/20">
                <div className="text-xs text-nb-text-muted mb-1">Total Changes</div>
                <div className="text-2xl font-bold text-nb-text-primary">{packData.diff_summary?.total_changes || 0}</div>
              </div>
              <div className="p-4 bg-nb-warning/10 rounded-lg border border-nb-warning/20">
                <div className="text-xs text-nb-text-muted mb-1">Milestone Threats</div>
                <div className="text-2xl font-bold text-nb-text-primary">{packData.impacts_summary?.total_milestone_threats || 0}</div>
              </div>
              <div className={`p-4 rounded-lg border ${
                packData.compile_status === 'pass' 
                  ? 'bg-nb-success/10 border-nb-success/20' 
                  : packData.compile_status === 'fail'
                  ? 'bg-nb-danger/10 border-nb-danger/20'
                  : 'bg-nb-warning/10 border-nb-warning/20'
              }`}>
                <div className="text-xs text-nb-text-muted mb-1">Compile Status</div>
                <div className="flex items-center gap-2">
                  {packData.compile_status === 'pass' ? (
                    <CheckCircle className="h-5 w-5 text-nb-success" />
                  ) : packData.compile_status === 'fail' ? (
                    <XCircle className="h-5 w-5 text-nb-danger" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-nb-warning" />
                  )}
                  <span className="text-lg font-bold capitalize text-nb-text-primary">{packData.compile_status}</span>
                </div>
              </div>
            </div>
            <div className="text-sm text-nb-text-muted">
              {packData.has_changes 
                ? `This week's schedule shows ${packData.diff_summary?.total_changes || 0} changes affecting milestones, critical path, and float.`
                : 'No changes detected this week.'}
            </div>
          </DarkPanel>

          {/* Changes This Week */}
          {packData.has_changes && (
            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nb-text-primary">
                <TrendingUp className="h-5 w-5" />
                Changes This Week
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-nb-text-muted">Activities</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-nb-text-muted">Added</span>
                      <span className="font-semibold text-nb-success">{packData.activities_added?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-nb-text-muted">Modified</span>
                      <span className="font-semibold text-nb-warning">{packData.activities_modified?.length || 0}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-nb-text-muted">Schedule Impact</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-nb-text-muted">Milestone Changes</span>
                      <span className="font-semibold text-nb-text-primary">{packData.milestone_changes?.length || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-nb-text-muted">Float Threshold Crossings</span>
                      <span className="font-semibold text-nb-danger">
                        {packData.float_changes?.filter((f: any) => f.crossed_threshold).length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </DarkPanel>
          )}

          {/* Milestone Threats */}
          {packData.milestone_threats.length > 0 && (
            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nb-text-primary">
                <AlertTriangle className="h-5 w-5 text-nb-warning" />
                Milestone Threats
              </h2>
              <div className="space-y-3">
                {packData.milestone_threats.slice(0, 10).map((mt: any, idx: number) => (
                  <div key={idx} className="p-4 bg-nb-warning/10 border border-nb-warning/20 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-semibold mb-1 text-nb-text-primary">{mt.milestone_name}</div>
                        <div className="text-sm text-nb-text-muted">
                          {mt.days_at_risk ? `${mt.days_at_risk} days at risk` : 'Risk identified'}
                        </div>
                      </div>
                      <Badge variant={mt.threat_level === 'high' ? 'destructive' : 'outline'}>
                        {mt.threat_level}
                      </Badge>
                    </div>
                    {mt.driving_changes && mt.driving_changes.length > 0 && (
                      <div className="text-xs text-nb-text-muted mt-2">
                        Driven by: {mt.driving_changes.slice(0, 3).join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </DarkPanel>
          )}

          {/* Critical Path Impacts */}
          {packData.critical_path_impacts.length > 0 && (
            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nb-text-primary">
                <TrendingDown className="h-5 w-5 text-nb-danger" />
                Critical Path Impacts
              </h2>
              <div className="space-y-2">
                {packData.critical_path_impacts.slice(0, 10).map((cpi: any, idx: number) => (
                  <div key={idx} className="p-3 bg-nb-danger/10 border border-nb-danger/20 rounded text-sm">
                    <div className="font-medium text-nb-text-primary">{cpi.activity_name || cpi.activity_id}</div>
                    <div className="text-xs text-nb-text-muted mt-1">{cpi.impact_type}</div>
                  </div>
                ))}
              </div>
            </DarkPanel>
          )}

          {/* Compile Status */}
          <DarkPanel className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nb-text-primary">
              <CheckCircle className="h-5 w-5" />
              Compile Status
            </h2>
            <div className="flex items-center gap-4 mb-4">
              <Badge variant={packData.compile_status === 'pass' ? 'default' : packData.compile_status === 'fail' ? 'destructive' : 'outline'}>
                {packData.compile_status.toUpperCase()}
              </Badge>
              <span className="text-sm text-nb-text-muted">
                {packData.violations_summary?.total_violations || 0} violations found
              </span>
            </div>
            {packData.violations.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-nb-text-muted">Top Violations</h3>
                {packData.violations.slice(0, 5).map((v: any, idx: number) => (
                  <div key={idx} className="p-3 bg-nb-surface-2 rounded text-sm">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-nb-text-primary">{v.rule_name}</div>
                      <Badge variant={v.severity === 'critical' ? 'destructive' : 'outline'} className="text-xs">
                        {v.severity}
                      </Badge>
                    </div>
                    <div className="text-xs text-nb-text-muted">{v.message}</div>
                  </div>
                ))}
              </div>
            )}
          </DarkPanel>

          {/* Actions Required */}
          {(packData.violations.filter((v: any) => v.severity === 'critical').length > 0 || 
            packData.milestone_threats.filter((mt: any) => mt.threat_level === 'high').length > 0) && (
            <DarkPanel className="p-6 border-2 border-nb-danger/30">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-nb-danger">
                <AlertTriangle className="h-5 w-5" />
                Actions Required
              </h2>
              <div className="space-y-3">
                {packData.violations.filter((v: any) => v.severity === 'critical').length > 0 && (
                  <div className="p-3 bg-nb-danger/20 rounded">
                    <div className="font-semibold text-nb-danger mb-1">
                      {packData.violations.filter((v: any) => v.severity === 'critical').length} Critical Violations
                    </div>
                    <div className="text-sm text-nb-text-secondary">
                      Immediate action required. Review violations in Compile Gate.
                    </div>
                  </div>
                )}
                {packData.milestone_threats.filter((mt: any) => mt.threat_level === 'high').length > 0 && (
                  <div className="p-3 bg-nb-warning/20 rounded">
                    <div className="font-semibold text-nb-warning mb-1">
                      {packData.milestone_threats.filter((mt: any) => mt.threat_level === 'high').length} High-Risk Milestones
                    </div>
                    <div className="text-sm text-nb-text-secondary">
                      Review milestone threats and develop mitigation plans.
                    </div>
                  </div>
                )}
              </div>
            </DarkPanel>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default DecisionPack;
